/** @format */

import { forwardRef, type ComponentProps } from 'react';
import { Filter } from 'lucide-react';
import { Button, cn } from '@clarix/ui';

export interface FilterButtonProps extends ComponentProps<typeof Button> {
  /** Number of active filters; renders a count badge when greater than 0. */
  activeCount?: number;
  /** Button label. Defaults to "Filter". */
  label?: string;
}

/**
 * The canonical "Filter" trigger button used across the app (Spark, Projects
 * list, Project activity, Calendar, …). One home for the filter button so every
 * surface looks identical: an outline `size="xs"` button (height 28px) with the
 * lucide `Filter` icon, a label, and a primary-tinted count badge when filters
 * are active.
 *
 * Designed to drop into any `asChild` trigger (`PopoverTrigger`,
 * `DropdownMenuTrigger`): it forwards its ref and spreads props onto the
 * underlying `Button`, so Radix can clone it and wire open/close state.
 */
export const FilterButton = forwardRef<HTMLButtonElement, FilterButtonProps>(function FilterButton(
  { activeCount = 0, label = 'Filter', className, ...props },
  ref,
) {
  return (
    <Button
      ref={ref}
      variant="outline"
      size="xs"
      className={cn('h-7 gap-1.5 text-lg', className)}
      {...props}
    >
      <Filter className="h-3.5 w-3.5" strokeWidth={1.5} />
      {label}
      {activeCount > 0 && (
        <span className="bg-primary text-primary-foreground ml-0.5 inline-flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-xs font-medium">
          {activeCount}
        </span>
      )}
    </Button>
  );
});
